// src/api/wordApi.ts
import { apiClient } from './axios';
import {
    BulkCreateWordsRequest,
    UpdateWordRequest,
    ReorderWordsRequest,
} from '../models/word/wordRequests';
import {
    BulkCreateWordsResponse,
    BulkDeleteWordsResponse,
    BulkUpdateDifficultyResponse,
} from '../models/word/wordResponse';
import {PaginationData} from "../models/paginationData.ts";
import {
    DictionaryMetadataBackfillReq,
    DictionaryMetadataBackfillJob,
    DictionaryWordFilterParams,
    DictionaryWordItem,
    DictionaryWordBulkUpsertReq,
    DictionaryWordBulkUpsertRes,
    BritishPhoneticPreview,
} from "../types/dictionary.ts";
import {AdminVocabResponse, AiTopicDifficulty} from "../models/word/aiVocabModels.ts";

// Word API
export const wordApi = {
    // 단어 일괄 생성
    bulkCreate: async (data: BulkCreateWordsRequest) => {
        return apiClient.post<BulkCreateWordsResponse>('/admin/words/bulk', data);
    },

    // AI: 단어 목록으로 뜻 생성
    aiGenerateFromList: async (wordbookId: number, data: {
        words: string[]; wordLang: string; meaningLang: string;
    }) => {
        return apiClient.post<AdminVocabResponse>(
            `/admin/wordbooks/${wordbookId}/ai/generate`, data,
        );
    },

    // AI: 주제로 단어 생성
    aiGenerateFromTopic: async (wordbookId: number, data: {
        topic: string; wordLang: string; meaningLang: string;
        wordCount: number; difficulty: AiTopicDifficulty;
    }) => {
        return apiClient.post<AdminVocabResponse>(
            `/admin/wordbooks/${wordbookId}/ai/topic`, data,
        );
    },

    // AI: 이미지에서 단어 추출 (multipart)
    aiExtractFromImage: async (
        wordbookId: number, image: File, wordLang: string, meaningLang: string,
    ) => {
        const form = new FormData();
        form.append('image', image);
        const qs = new URLSearchParams({wordLang, meaningLang}).toString();
        return apiClient.postMultipart<AdminVocabResponse>(
            `/admin/wordbooks/${wordbookId}/ai/extract?${qs}`, form,
        );
    },

    // 단어 수정
    update: async (wordId: number, data: UpdateWordRequest) => {
        return apiClient.putNoData(`/admin/words/${wordId}`, data);
    },

    // 단어 삭제 (소프트 삭제)
    delete: async (wordId: number) => {
        return apiClient.deleteNoData(`/admin/words/${wordId}`);
    },

    // 단어 영구 삭제
    permanentDelete: async (wordId: number) => {
        return apiClient.deleteNoData(`/admin/words/${wordId}/permanent`);
    },

    // 단어 복원
    restore: async (wordId: number) => {
        return apiClient.postNoData(`/admin/words/${wordId}/restore`);
    },

    // 단어 순서 변경
    updateSortOrder: async (wordId: number, sortOrder: number) => {
        return apiClient.patchNoData(`/admin/words/${wordId}/sort-order`, { sortOrder });
    },

    // 단어 일괄 삭제
    bulkDelete: async (wordIds: number[]) => {
        return apiClient.post<BulkDeleteWordsResponse>(
            '/admin/words/bulk-delete',
            { wordIds }
        );
    },

    // 단어 일괄 난이도 변경
    bulkUpdateDifficulty: async (wordIds: number[], difficulty: number) => {
        return apiClient.post<BulkUpdateDifficultyResponse>(
            '/admin/words/bulk-difficulty',
            { wordIds, difficulty }
        );
    },

    // 단어 순서 재정렬
    reorder: async (data: ReorderWordsRequest) => {
        return apiClient.postNoData('/admin/words/reorder', data);
    },

    getDictionaryWords: async (params: DictionaryWordFilterParams) => {
        const {page, size, onlyMissing, backfillExhausted, word, languageCode, sourceType, sort, direction} = params;
        return apiClient.get<PaginationData<DictionaryWordItem>>('/admin/words/dictionary', {
            page: page - 1,
            size,
            onlyMissing,
            ...(backfillExhausted ? {backfillExhausted} : {}),
            ...(word ? {word} : {}),
            ...(languageCode ? {languageCode} : {}),
            ...(sourceType ? {sourceType} : {}),
            ...(sort ? {sort} : {}),
            ...(direction ? {direction} : {}),
        });
    },

    updateDictionaryWord: async (id: number, data: { translation?: string; translationLang?: string; pos?: string; difficultyScore?: number }) => {
        return apiClient.patch<DictionaryWordItem>(`/admin/words/dictionary/${id}`, data);
    },

    backfillDictionaryMetadata: async (data: DictionaryMetadataBackfillReq) => {
        return apiClient.post<DictionaryMetadataBackfillJob>('/admin/words/dictionary/backfill-metadata', data);
    },

    getDictionaryBackfillJobs: async (page: number = 1, size: number = 20, inProgressOnly: boolean = false) => {
        return apiClient.get<PaginationData<DictionaryMetadataBackfillJob>>('/admin/words/dictionary/backfill-metadata/jobs', {
            page: page - 1,
            size,
            inProgressOnly,
        });
    },

    cancelDictionaryBackfillJob: async (jobId: number) => {
        return apiClient.post<DictionaryMetadataBackfillJob>(`/admin/words/dictionary/backfill-metadata/jobs/${jobId}/cancel`);
    },

    // 영국식 → 미국식(GenAm) 발음기호 재생성 dry-run (적용 없이 후보수 + 샘플)
    previewRegeneratePhoneticUs: async () => {
        return apiClient.get<BritishPhoneticPreview>('/admin/words/dictionary/regenerate-phonetic-us/preview');
    },

    // 영국식 → 미국식 재생성 잡 트리거 (mode 는 서버에서 강제 주입)
    regeneratePhoneticUs: async (data: DictionaryMetadataBackfillReq) => {
        return apiClient.post<DictionaryMetadataBackfillJob>('/admin/words/dictionary/regenerate-phonetic-us', data);
    },

    // Dictionary CSV
    exportDictionaryWords: async (params?: {word?: string; languageCode?: string; onlyMissing?: boolean; limit?: number}) => {
        return apiClient.get<DictionaryWordItem[]>('/admin/words/dictionary/export', {
            ...(params?.word ? {word: params.word} : {}),
            ...(params?.languageCode ? {languageCode: params.languageCode} : {}),
            onlyMissing: params?.onlyMissing ?? false,
            ...(params?.limit ? {limit: params.limit} : {}),
        });
    },

    bulkUpsertDictionaryWords: async (data: DictionaryWordBulkUpsertReq) => {
        return apiClient.post<DictionaryWordBulkUpsertRes>('/admin/words/dictionary/bulk-upsert', data);
    },

    resetDictionaryBackfillAttempts: async (id: number) => {
        return apiClient.post<DictionaryWordItem>(`/admin/words/dictionary/${id}/reset-backfill-attempts`);
    },
};
